import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {FlatpickrOptions} from '../../vendor/ng2-flatpickr';
import {Field} from '../../interfaces/field';
import moment from 'moment-timezone';
import {Subscription} from 'rxjs';
import {faCalendar, faCheck, faClock, faInfoCircle, faPlaneDeparture} from '@fortawesome/free-solid-svg-icons';
import {FieldErrorService} from '../../services/field-error.service';
import {StateService} from '../../services/state.service';
import {Moment} from 'moment';
import {faPlaneArrival} from "@fortawesome/free-solid-svg-icons/faPlaneArrival";

let isArray = function (a) {
  return (!!a) && (a.constructor === Array);
};
// Maps the moment-token formats offered in the portal's general settings
// (general.component.ts `dateFormats`) to their flatpickr equivalents.
const MOMENT_TO_FLATPICKR_DATE_FORMAT: {[momentFormat: string]: string} = {
  'DD-MM-YYYY': 'd-m-Y',
  'DD/MM/YYYY': 'd/m/Y',
  'YYYY-MM-DD': 'Y-m-d',
  'YYYY/MM/DD': 'Y/m/d',
  'MMM Do YYYY': 'M J Y',
  'MM/DD/YYYY': 'm/d/Y',
};
const pad = (num: number): string => String(num).length < 2 ? '0' + num : '' + num;
@Component({
    selector: 'app-date-time-field',
    templateUrl: './date-time-field.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./date-time-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateTimeFieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('picker', {static: true}) element;
  @ViewChild('desktopTimePicker', {static: true}) element2;
  // @ViewChild('datePicker', { static: true }) datePicker;
  @ViewChild('timePicker', {static: true}) timePicker;
  @Input() config: Field;
  @Input() airportDates: string;
  @Input() control: UntypedFormGroup;
  @Input() summary = false;
  @Input() orderMode: string;
  @Input() configRequestedDestinationDateOffset: number;
  @Input() airportDeparture: boolean;
  @Input() airportDestination: boolean;
  @Input() minimumOrderTime = 60;
  @Input() orderTimesHandler = 'availability';
  @Output() doToggleHelpText: EventEmitter<any> = new EventEmitter<any>();

  isReturnTrip = false;
  options2: FlatpickrOptions = {
    'weekNumbers': true,
    'enableTime': false,
    'time_24hr': true,
    'minuteIncrement': 1,
    'allowInput': false,
    'wrap': true,
    'onClose': this.onClose.bind(this),
    'onOpen': this.onOpen.bind(this),
    'onChange': this.onChange.bind(this),
    'defaultDate': new Date(),
    'locale': {
      firstDayOfWeek: 1
    },
    'minDate': new Date(),
    'dateFormat': 'd-m-Y',
    'utc': false
  };
  timeDisplay = 'H';
  mode = 'requestedDate';
  opened = false;
  forcedDate: string;
  requestedDateChanges: Subscription;
  airportDestinationChanges: Subscription;
  minDate: string;
  hasErrors = false;

  mobileMinDateString = '';
  mobileDateString = '';
  mobileMinTimeString = '';
  mobileTimeString = '';

  selectedDate = '';
  selectedTime = '';
  selectedClock = '';

  faCheck = faCheck;
  faClock = faClock;
  faCalendar = faCalendar;
  faInfoCircle = faInfoCircle;

  days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  allowedDays = [];
  timeHourOptions = [];
  timeMinutesOptions = [];
  clockOptions = ['AM', 'PM'];
  minTime = '00:00';
  maxTime = '23:55';
  fullyDisabledDays = [];
  overrideEnabledDays = [];
  hasOptions = true;
  fieldTitle = 'requested_date_field_label';
  minRequestedDate: any;
  protected readonly faPlaneArrival = faPlaneArrival;
  protected readonly faPlaneDeparture = faPlaneDeparture;
  protected readonly Math = Math;

  constructor(private errorService: FieldErrorService,
              public _state: StateService) {
    this.normalizeLegacyThursdayKey();
    const configuredDateFormat = MOMENT_TO_FLATPICKR_DATE_FORMAT[this._state.companySettings.dateFormat];
    if (configuredDateFormat) {
      this.options2.dateFormat = configuredDateFormat;
    }

    this.airportDestinationChanges =
      this._state.airportDestination$
        .subscribe(() => {
          this.setDropDownOption();
        });
  }

  /**
   * Older availability settings (still written by portal.yourdriverapp.com /
   * api.dispatchapi.io) use the key set mon, thu, wed, thr, fri, sat, sun: "thu" is TUESDAY,
   * "thr" is THURSDAY and there is no "tue". Rename them to the real weekday keys
   * (thu -> tue, thr -> thu) so every lookup by weekday name works. A lone "thr" next to a
   * real "tue" is only the misspelled Thursday: move it to "thu" (never overwriting one).
   */
  private normalizeLegacyThursdayKey(): void {
    const availableDays = this._state.companySettings?.availability?.availableDays;
    if (!availableDays || availableDays['thr'] === undefined) {
      return;
    }
    const legacyScheme = availableDays['tue'] === undefined;
    const hasThu = availableDays['thu'] !== undefined;
    const rebuilt = {};
    Object.keys(availableDays).forEach((key) => {
      if (legacyScheme && key === 'thu') {
        rebuilt['tue'] = availableDays[key];
      } else if (key === 'thr') {
        if (legacyScheme || !hasThu) {
          rebuilt['thu'] = availableDays[key];
        }
      } else {
        rebuilt[key] = availableDays[key];
      }
    });
    this._state.companySettings.availability.availableDays = rebuilt;
  }

  ngAfterViewInit(): void {
    this.ngOnInit();
    const self = this;

    this.minDate = this.getMinRequestedDate();
    let dateClientTZ = this.minDate.toString();
    let splitDate = dateClientTZ.split('T');

    let minDate: Date;
    if (splitDate[0] !== moment().format('YYYY-MM-DD')) {
      minDate = moment(`${splitDate[0]}T${splitDate[1].substr(0, 8)}`).tz(this._state.timeZone).toDate();
    } else {
      minDate = moment(`${splitDate[0]}T${splitDate[1].substr(0, 8)}`).tz(this._state.timeZone).add(5, 'minutes').toDate();
    }

    if (this.control.controls[this.mode].value && this._state.companySettings.clock === '12') {
      this.selectedClock = moment(this.control.controls[this.mode].value).tz(this._state.timeZone).format('A');
    }

    let date: Date;
    if (
      !this.control.controls[this.mode].value ||
      moment(this.control.controls[this.mode].value).tz(this._state.timeZone).isBefore(moment(minDate).tz(this._state.timeZone)) ||
      this.control.controls[this.mode].value === 'Invalid date'
    ) {
      date = minDate;
    } else {
      dateClientTZ = this.control.controls[this.mode].value.toString();
      splitDate = dateClientTZ.split('T');
      date = moment(`${splitDate[0]}T${splitDate[1].substr(0, 8)}`).tz(this._state.timeZone).toDate();
    }

    if (moment(date).isBefore(moment(minDate))) {
      date = minDate;
    }

    this.element.flatpickr.set('defaultDate', date);
    this.element.flatpickr.set('firstDayOfWeek', 1);
    this.element.flatpickr.set('minDate', minDate);
    this.element.flatpickr.setDate(date, false, 'd-m-Y ' + this.timeDisplay + ':i');
    // 🔥 Now just call it and let it do its thing
    this.getAllowedAndDisabledDates();

    this.element.flatpickr.set('disable', [
      function (date: Date) {
        const formatted = moment(date).format('YYYY-MM-DD');

        if (
          moment(date).isAfter(self.minDate) &&
          formatted === moment().format('YYYY-MM-DD')
        ) {
          return true;
        }

        if (self.overrideEnabledDays.includes(formatted)) {
          return false;
        }

        return (
          self.allowedDays.includes(date.getDay()) ||
          self.fullyDisabledDays.includes(formatted)
        );
      },
    ]);
    this.selectedDate = moment(date).format('YYYY-MM-DD');
    this.selectedTime = moment(date).format(this.timeDisplay + this.timeDisplay + ':mm');
    this.onChange([date]);
  }

  setTimeSelection() {
    const self = this;
    // console.group('DateLimits:', this.config.property)
    const minSplit = this.getMinSplitForSelectedDate();


    this.generateTimeOptions();

  }

  getAllowedAndDisabledDates(): void {
    const self = this;
    self.allowedDays = [];
    self.fullyDisabledDays = [];
    const availabilityDays = self._state.companySettings.availability?.availableDays || {};
    const availabilityOverrides = self._state.companySettings.availabilityOverrides || [];

    // Normalize old structure
    if (availabilityDays && !availabilityDays[this.days[0]]) {
      const newSettings = {};
      Object.keys(availabilityDays).forEach((key) => {
        const day = key.split('_')[1];
        newSettings[day] = {
          active: availabilityDays[key],
          start: '00:00',
          end: '24:00'
        };
      });
      self._state.companySettings.availability.availableDays = newSettings;
    }

    // Build allowedDays (disabled weekdays)
    // The weekday comes from the key NAME (days[] is Sunday-first, same as Date.getDay()), never
    // from the key's position: a config whose keys are not in mon..sun order, or that omits a
    // day, would otherwise shift every weekday by one. Same lookup as api.dispatchapi.io /
    // api.taxiid.nl and the rest of this component.
    Object.keys(self._state.companySettings.availability.availableDays).forEach((key) => {
      const dayData = self._state.companySettings.availability.availableDays[key];
      const weekday = self.days.indexOf(key);
      if (weekday === -1) {
        return;
      }

      if (Array.isArray(dayData)) {
        dayData.forEach((slot) => {
          if (!slot.active && !self.allowedDays.includes(weekday)) {
            self.allowedDays.push(weekday);
          }
        });
      } else if (!dayData.active || dayData === 'n') {
        if (!self.allowedDays.includes(weekday)) {
          self.allowedDays.push(weekday);
        }
      }
    });
    // Build fullyDisabledDays / overrideEnabledDays from overrides
    self.overrideEnabledDays = [];
    availabilityOverrides.forEach((override) => {
      if (override.type === 'available') {
        // Mark every date in the range as explicitly available so the calendar
        // shows them even if the weekday is normally inactive.
        const cursor = moment(override.startDate).startOf('day');
        const endDay = moment(override.endDate).startOf('day');
        while (cursor.isSameOrBefore(endDay, 'day')) {
          self.overrideEnabledDays.push(cursor.format('YYYY-MM-DD'));
          cursor.add(1, 'day');
        }
        return;
      }

      // type === 'unavailable' — existing logic
      const sDate = moment(override.startDate);
      const eDate = moment(override.endDate);
      // If it ends at 23:59 or later, bump to next day 00:00
      if (eDate.format('HH:mm') >= '23:56') {
        eDate.add(1, 'day').startOf('day');
      }
      const dayDiff = eDate.diff(sDate, 'days');

      if (dayDiff > 1) {
        for (let x = 1; x < dayDiff; x++) {
          self.fullyDisabledDays.push(sDate.clone().add(x, 'days').format('YYYY-MM-DD'));
        }
      } else if (sDate.format('HH:mm') && dayDiff === 1) {
        self.fullyDisabledDays.push(sDate.format('YYYY-MM-DD'));
      }
    });

    // Log if needed
  }

  getMinSplitForSelectedDate(): [number, number] {
    const minSplit: [number, number] = [0, 0];
    let minSplitString = this.minTime.split(':');
    const calculatedMinTime = moment(`${this.selectedDate}T${this.minTime}`).tz(this._state.timeZone).format('HH:mm');
    const minDateFormatted = moment(this.minDate).tz(this._state.timeZone).format('YYYY-MM-DD');
    const selectedDateFormatted = moment(this.selectedDate).tz(this._state.timeZone).format('YYYY-MM-DD');
    const todayFormatted = moment().tz(this._state.timeZone).format('YYYY-MM-DD');
    const tomorrowFormatted = moment().add(1, 'days').tz(this._state.timeZone).format('YYYY-MM-DD');

    if (
      (this.orderTimesHandler === 'always' && !this.firstAllowedDaySelected()) ||
      (this.orderTimesHandler === 'always_forward_time' && minDateFormatted !== selectedDateFormatted)
    ) {
      if (
        this.orderTimesHandler === 'always_forward_time' &&
        selectedDateFormatted === tomorrowFormatted &&
        minDateFormatted !== todayFormatted
      ) {
        minSplitString = calculatedMinTime.split(':');
        minSplit[0] = parseInt(minSplitString[0], 10);
        minSplit[1] = parseInt(minSplitString[1], 10);
      } else {
        minSplit[0] = 0;
        minSplit[1] = 0;
      }
    } else if (
      this.orderTimesHandler === 'always_forward_time' &&
      this.firstAllowedDaySelected()
    ) {
      const calculated = calculatedMinTime.split(':');
      const minTimeSplit = this.minTime.split(':');

      const calculatedHour = parseInt(calculated[0], 10);
      const calculatedMinute = parseInt(calculated[1], 10);
      const minTimeHour = parseInt(minTimeSplit[0], 10);
      const minTimeMinute = parseInt(minTimeSplit[1], 10);

      const selectedMoment = moment(`${this.selectedDate}T${this.selectedTime}`).tz(this._state.timeZone);
      const minMoment = moment(this.minDate).tz(this._state.timeZone);

      if (
        calculatedHour < minTimeHour ||
        (calculatedHour === minTimeHour && calculatedMinute <= minTimeMinute)
      ) {
        if (selectedMoment.isBefore(minMoment)) {
          this.selectedTime = moment(`${this.selectedDate}T${calculatedMinTime}:00`)
            .add(5, 'minutes')
            .format(`${this.timeDisplay}${this.timeDisplay}:mm`);
        }

        minSplit[0] = calculatedHour;
        minSplit[1] = calculatedMinute;
      } else {
        minSplit[0] = minTimeHour;
        minSplit[1] = minTimeMinute;
      }
    } else {
      minSplit[0] = parseInt(minSplitString[0], 10);
      minSplit[1] = parseInt(minSplitString[1], 10);
    }

    return minSplit;
  }

  generateTimeOptions() {
    const minMoment = moment(this.minDate).tz(this._state.timeZone);
    const selectedMoment = moment.tz(`${this.selectedDate}T00:00:00`, this._state.timeZone);
    const is12Clock = this._state.companySettings.clock === '12';
    const crossedMin = selectedMoment.isAfter(minMoment, 'day') || selectedMoment.isSame(minMoment, 'day');

    let selectedDay = selectedMoment.format('ddd').toLowerCase();
    if (selectedDay === 'tue') selectedDay = 'thu';

    let availability = this._state.companySettings.availability.availableDays?.[selectedDay] || [];
    let timeSlots = Array.isArray(availability)
      ? availability.filter(slot => slot.active)
      : (availability.active ? [availability] : []);

    const overrides = (this._state.companySettings.availabilityOverrides || []).filter(o => {
      const start = moment(o.startDate).tz(this._state.timeZone).startOf('day');
      const end = moment(o.endDate).tz(this._state.timeZone).endOf('day');
      return selectedMoment.isBetween(start, end, null, '[]');
    });

    overrides.forEach(o => {
      const start = moment(o.startDate).tz(this._state.timeZone).format('HH:mm');
      const end = moment(o.endDate).tz(this._state.timeZone).format('HH:mm');
      if (o.type === 'available') {
        timeSlots.push({ start, end, active: true });
      } else if (o.type === 'unavailable') {
        // Split slots around the unavailable period
        const newTimeSlots = [];
        timeSlots.forEach(slot => {
          // No overlap - keep the slot as is
          if (slot.end <= start || slot.start >= end) {
            newTimeSlots.push(slot);
          } else {
            // Overlap exists - split the slot
            // Keep the part before the override
            if (slot.start < start) {
              newTimeSlots.push({ ...slot, end: start });
            }
            // Keep the part after the override
            if (slot.end > end) {
              newTimeSlots.push({ ...slot, start: end });
            }
          }
        });
        timeSlots = newTimeSlots;
      }
    });

    this.timeHourOptions = [];
    this.timeMinutesOptions = [];

    for (let h = 0; h < 24; h++) {
      const hourStr = pad(h);
      const displayVal = is12Clock
        ? `${pad((h % 12) || 12)} ${h < 12 ? 'AM' : 'PM'}`
        : hourStr;

      const inSlot = timeSlots.some(slot => {
        const [startHour] = (slot.start || '00:00').split(':').map(Number);
        let [endHour] = (slot.end || '24:00').split(':').map(Number);
        endHour = (endHour === 0 ? 24 : endHour);
        return h >= startHour && h < endHour;
      });
      const currentHourMoment = moment.tz(`${this.selectedDate}T${hourStr}:59`, this._state.timeZone);
      let enabled = inSlot;

      if ((this.orderTimesHandler === 'always_forward_time' || this.orderTimesHandler === 'always')) {
        if(inSlot) {
          enabled = (currentHourMoment.isSameOrAfter(minMoment));
        } else {
          enabled = true;
        }
      } else if (this.orderTimesHandler === 'availability') {
        enabled = inSlot && currentHourMoment.isSameOrAfter(minMoment);
      }
      this.timeHourOptions.push({
        value: hourStr,
        selectedValue: pad(h),
        text: displayVal,
        enabled
      });
    }

    const selectedHour = parseInt(this.selectedTime.split(':')[0], 10);
    for (let i = 0; i < 60; i += 5) {
      const minuteMoment = moment.tz(`${this.selectedDate}T${pad(selectedHour)}:${pad(i)}`, this._state.timeZone);
      const enabled = minuteMoment.isSameOrAfter(minMoment);
      this.timeMinutesOptions.push({
        value: pad(i),
        enabled
      });
    }

  }

  ngOnInit() {
    // this.minDate = this.getMinRequestedDate();
    if (this.config.property?.startsWith('return')) {
      this.isReturnTrip = true;
    }
    this.mode = this.config.property;
    // console.log(`${this.mode} - ${this.control.controls[this.mode].value}`);
    this.setDropDownOption();
  }

  setDropDownOption() {
    let optionCnt = 0;
    if (this.airportDates !== 'none') {
      if (this.orderMode !== 'airport') {
        optionCnt++;
      } else if (this.airportDates === 'optional') {
        optionCnt++;
      }
      if ((this.orderMode === 'airport' && this._state.airportDestination) && !this._state.airportDeparture) {
        optionCnt++;
      }
      if (this.orderMode === 'airport' && this._state.airportDeparture) {
        optionCnt++;
      }
    }
    if (optionCnt > 1) {
      this.hasOptions = true;
    } else {
      this.hasOptions = false;
    }
  }

  convertDateToCompanyTimeZone(date: string): Moment {
    // console.log(`###############################`);
    if (typeof (this._state.timeZone) === 'undefined') {
      console.log(this._state);
    }
    let offSet, userDate;
    if (`${moment(date).format('Z')}` !== `${moment(date).tz(this._state.timeZone).format('Z')}`) {
      offSet = (moment(date).utcOffset() - moment(date).tz(this._state.timeZone).utcOffset()) / 60;
      userDate = moment(date).tz(this._state.timeZone).add(offSet, 'hours');
    } else {
      userDate = moment(date).tz(this._state.timeZone);
    }
    // console.log(`userDate: ${moment(userDate).toDate()}`);
    // console.log(`userDate: ${moment(userDate).tz()}`);
    // console.log(`userDate: ${moment(userDate).format()}`);
    return moment(userDate);
  }

  getMinRequestedDate(): string {
    const self = this;
    let newDate, currentDate = moment();

    if (!self._state.companySettings.availability) {
      self._state.companySettings.availability = {};
    }

    if (!self._state.companySettings.availability.availableDays) {
      this.orderTimesHandler = 'always';
    }

    if (this.config.property === 'returnRequestedDate') {
      newDate = moment().tz(this._state.timeZone).add(moment(this.control.controls['requestedDate'].value).diff(moment().tz(this._state.timeZone), 'minutes'), 'minutes');
    } else {
      newDate = moment().tz(this._state.timeZone).add(this.minimumOrderTime, 'minutes');
    }
    // console.log(newDate.format());

    /**
     * Are we before the first timeslot?
     * Or are we closed today?
     */

    /**
     * Create moment object for today at start using
     * self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][0].start
     */
    if (self._state.companySettings && self._state.companySettings.availability &&
      self._state.companySettings.availability.availableDays &&
      self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]] &&
      self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]] &&
      self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][0] &&
      self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][0].start) {
      let splitStart = self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][0].start.split(':');
      // console.log('OrderTimesHandler:', this.orderTimesHandler);
      // console.log('availability:', self._state.companySettings.availability);
      if (this.orderTimesHandler !== 'always' &&
        (self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][0].active &&
          currentDate.isBefore(moment().hour(splitStart[0]).minute(splitStart[1])) || !self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][0].active)) {
        /**
         * Are we before the fist timeslot?
         */
        newDate = self.findFirstAvailableStartOfTimeslot(currentDate);
      } else if (self._state.companySettings && self._state.companySettings.availability && self._state.companySettings.availability.availableDays && self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]] && this.orderTimesHandler !== 'always') {
        const lastdaySlot = self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]].length - 1;
        if (this.orderTimesHandler !== 'always' &&
          self._state.companySettings && self._state.companySettings.availability &&
          self._state.companySettings.availability.availableDays &&
          self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]] &&
          self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][lastdaySlot] &&
          self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][lastdaySlot].end) {
          if (self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][lastdaySlot].end === '00:00') {
            self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][lastdaySlot].end = '23:59';
          }

          let lastTimeToday = moment(`${currentDate.format('YYYY-MM-DD')}T${self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][lastdaySlot].end}:00`).tz(this._state.timeZone);

          if (!self._state.companySettings.availability.availableDays[self.days[currentDate.weekday()]][lastdaySlot].active) {
            lastTimeToday = moment(`${currentDate.format('YYYY-MM-DD')}T00:00`).tz(this._state.timeZone);
          }


          /**
           * Is the end before now? then we move to the next date to determine the newDate
           */
          if (lastTimeToday.isBefore(currentDate)) {
            newDate = self.findFirstAvailableStartOfTimeslot(currentDate);
          } else {
          }
          let date = moment(newDate).tz(this._state.timeZone).format('YYYY-MM-DD');
          let minTime = moment(`${date}T${self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][lastdaySlot].end}:00`).tz(this._state.timeZone, true);

          const checkDate = (this.orderTimesHandler === 'availability' ? newDate : currentDate);
          if (minTime.isBefore(checkDate) || !self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][lastdaySlot].active) {
            for (let x = 1; x++; x < 8) {
              newDate = newDate.add(1, 'days');
              if (self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][0].active) {
                break;
              }
            }

            newDate = moment(`${newDate.format('YYYY-MM-DD')}T${self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][0].start}:00`).tz(this._state.timeZone);

            if (this.orderTimesHandler === 'always_forward_time') {
              newDate = newDate.add(this.minimumOrderTime, 'minutes');
            }
          }
        }
      }
    }

    /**
     * Check if minTime is within a closed window, update the minOrdertime and run this function again
     */

    if (
      this._state.companySettings.availabilityOverrides?.length &&
      this.minimumOrderTime < 2000
    ) {
      for (const override of this._state.companySettings.availabilityOverrides) {

        const start = moment(override.startDate).tz(this._state.timeZone);
        const end   = moment(override.endDate).tz(this._state.timeZone);

        const requested = newDate.clone().tz(this._state.timeZone);

        if (requested.isBetween(start, end, undefined, '[)')) {

          // Minutes from NOW until end of closed window
          const minutesToEnd = end.diff(moment().tz(this._state.timeZone), 'minutes');

          // Guard against negative / zero
          if (minutesToEnd > 0) {
            this.minimumOrderTime = minutesToEnd + 1; // push past closure
          }

          // Recalculate once and EXIT
          newDate = moment(this.getMinRequestedDate()).tz(this._state.timeZone);
          break;
        }
      }
      /**
       * Round newDate to the next full 5 minutes
       */
      newDate = this.roundToNext5Minutes(newDate);
      const selectedDate = newDate.format('YYYY-MM-DD');
      const selectedTime = newDate.format('' + this.timeDisplay + '' + this.timeDisplay + ':mm');

      return this.convertDateToCompanyTimeZone(`${selectedDate}T${selectedTime}`).format();
    } else {

      newDate = this.roundToNext5Minutes(newDate);

      const selectedDate = newDate.format('YYYY-MM-DD');
      const selectedTime = newDate.format('' + this.timeDisplay + '' + this.timeDisplay + ':mm');

      return this.convertDateToCompanyTimeZone(`${selectedDate}T${selectedTime}`).format();
    }
  }

  roundToNext5Minutes(newDate) {
    const d = moment(newDate).tz(this._state.timeZone).toDate();
    let ms = 1000 * 60 * 5; // convert minutes to ms
    return moment(Math.ceil(d.getTime() / ms) * ms).tz(this._state.timeZone);
  }

  findFirstAvailableStartOfTimeslot(currentDate) {
    const self = this;
    let itt = 0;

    // current "now + minimumOrderTime" in desired zone
    const minReadyDate = moment.tz(currentDate || undefined, self._state.timeZone)
      .add(self.minimumOrderTime, 'minutes');

    let checkDate = minReadyDate.clone().startOf('day');

    while (itt < 7) {
      const dayKey = self.days[checkDate.weekday()];
      const daySlots = self._state.companySettings.availability.availableDays[dayKey];

      if (daySlots?.length) {
        for (const slot of daySlots) {
          if (!slot.active) continue;

          const [startHour, startMinute] = slot.start.split(':').map(Number);
          const slotStart = checkDate.clone().set({ hour: startHour, minute: startMinute, second: 0 });

          if (slotStart.isSameOrAfter(minReadyDate)) {
            return slotStart;
          }
        }
      }

      checkDate = checkDate.add(1, 'day');
      itt++;
    }

    console.warn('❌ No valid timeslot found within 7 days.');
    return null;
  }

  onChange(date: any) {
    const self = this;

    const newDate = moment(date[0]);
    this.selectedDate = newDate.format('YYYY-MM-DD');
    const patch = {};

    const submitDate = this.convertDateToCompanyTimeZone(`${this.selectedDate}T${this.selectedTime}`);

    const tooEarly = this.isReturnTrip
      ? submitDate.isBefore(moment(this.minDate).tz(this._state.timeZone))
      : moment().tz(this._state.timeZone).add(this.minimumOrderTime, 'minutes').isAfter(moment(submitDate));

    if (tooEarly) {
      return false;
    }

    patch[this.mode] = submitDate.format();
    this.control.patchValue(patch);

    let minTime;
    if (self._state.companySettings && self._state.companySettings.availability && self._state.companySettings.availability.availableDays && self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]] && this.orderTimesHandler === 'availability') {
      /**
       * Update availability settings to new format?
       */
      if (!isArray(self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]])) {
        Object.keys(self._state.companySettings.availability.availableDays).forEach((day) => {
          const dayValue = self._state.companySettings.availability.availableDays[day];
          self._state.companySettings.availability.availableDays[day] = [dayValue];
        })
      }

      const firstInterval = 0;
      const lastInterval = self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]].length - 1;

      minTime = moment(`${this.selectedDate}T${self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][firstInterval].start}:00`);

      let endTime = self._state.companySettings.availability.availableDays[self.days[newDate.weekday()]][lastInterval].end;
      if (endTime === '24:00') endTime = '23:59';

      let maxTime = moment(`${this.selectedDate}T${endTime}`).tz(this._state.timeZone, true);

      if (maxTime.isBefore(moment(this.minDate).tz(this._state.timeZone))) {
        maxTime = moment(this.minDate).tz(this._state.timeZone);
      }
      this.maxTime = maxTime.format('' + this.timeDisplay + '' + this.timeDisplay + ':mm');
    } else {
      this.minTime = '00:00';
      minTime = moment(`${this.selectedDate}T00:00:00`).tz(this._state.timeZone, true);

      this.maxTime = '23:59';
    }

    if (minTime.isBefore(moment(this.minDate).tz(this._state.timeZone))) {
      minTime = moment(this.minDate).tz(this._state.timeZone);
    }

    this.minTime = minTime.format('' + this.timeDisplay + '' + this.timeDisplay + ':mm');

    if (moment(`${this.selectedDate}T${this.selectedTime}`).tz(this._state.timeZone).isBefore(moment(`${this.selectedDate}T${this.minTime}`).tz(this._state.timeZone).toDate()) || moment(`${this.selectedDate}T${this.selectedTime}`).tz(this._state.timeZone).isAfter(moment(`${this.selectedDate}T${this.maxTime}`).tz(this._state.timeZone).toDate())) {
      this.selectedTime = minTime.format('' + this.timeDisplay + '' + this.timeDisplay + ':mm');
    }

    this.setTimeSelection();
  }

  onChangeTime($event) {

    let selectedTimeSplit = this.selectedTime.split(':');
    if ($event.target.name === 'hours') {
      this.selectedTime = `${$event.target.value}:${selectedTimeSplit[1]}:00`;
    } else if ($event.target.name === 'minutes') {
      this.selectedTime = `${selectedTimeSplit[0]}:${$event.target.value}:00`;
    } else if ($event.target.name === 'clock') {
      this.selectedClock = $event.target.value;
    }
    selectedTimeSplit = this.selectedTime.split(':');
    if(this._state.companySettings.clock === '12') {
      this.selectedClock = parseInt(selectedTimeSplit[0]) >= 12 ? 'PM' : 'AM';
    }

    let newDate = moment(this.selectedDate + 'T' + this.selectedTime + '').tz(this._state.timeZone);
    const selectedDate = newDate.format('YYYY-MM-DD');
    const patch = {};
    const submitDate = this.convertDateToCompanyTimeZone(`${selectedDate}T${this.selectedTime}`);
    patch[this.mode] = submitDate.format();

    this.control.patchValue(patch);

    this.setTimeSelection();
  }

  onClose() {
    this.opened = false;
  }

  onOpen() {
    this.opened = true;
  }

  changeMode(event: any) {

    const patch = {};
    let targetValue = event.target.value;

    if (this.config.property.indexOf('return') !== -1) {
      targetValue = event.target.value.replace('requested', 'returnRequested');
    }
    if (targetValue !== this.mode) {
      patch[targetValue] = this.control.controls[this.mode].value;
      // this.control.controls[targetValue].setValue(this.control.controls[this.mode].value);
      if (this.mode === 'requestedDestinationDate' || this.mode === 'returnRequestedDestinationDate') {
        this.control.controls[this.mode].setValue(null);
        patch[this.mode] = null;
        this.control.patchValue(patch);
      }
    }
    // console.log(this.control.controls[`${this.mode}Mode`].value);
    this.mode = targetValue;
    patch[this.mode] = this.control.controls[this.mode].value;
    if (this.isReturnTrip) {
      patch['returnRequestedDateMode'] = this.mode;
    } else {
      patch['requestedDateMode'] = this.mode;
    }


    this._state.forcedRequestedDateMode = this.mode;
    this.control.patchValue(patch);

  }

  setNow() {
    const patch = {};
    const now = this.getMinRequestedDate();
    this.forcedDate = now;
    patch[this.mode] = now;
    this.control.patchValue(patch);
  }

  validityChanged(valid: boolean) {
    this.hasErrors = valid;
  }

  ngOnDestroy(): void {
    if (this.requestedDateChanges) {
      this.requestedDateChanges.unsubscribe();
    }
  }

  getHelpText() {
    if (this.orderMode !== 'airport' || this.airportDates === 'optional') {
      return this.config.helpText;
    } else if (this.mode === 'requestedDestinationDate') {
      return 'requesteddestinationdate_date_field_help_text';
    } else if (this.mode === 'requestedScheduleDate') {
      return 'requestedscheduledate_date_field_help_text';
    } else {
      return this.config.helpText;
    }
  }

  todaySelected() {
    return (moment(this.selectedDate).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD'));
  }

  firstAllowedDaySelected() {
    return (moment(this.selectedDate).format('YYYY-MM-DD') === moment(this.minDate).format('YYYY-MM-DD'));
  }
}
